import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useOrganization } from "../../../hooks/queries";
import { LocationData } from "../../../models/LocationData";
import DataSvc from "../../../services/dataSvc";
import {
  buildCsvFilename,
  insertOrgAndFacilityIds,
} from "../../../utils/csvUtils";
import { TextFileDownload } from "../../Common/TextFileDownload";
import { TimespanPicker } from "../TimespanPicker";
import { useTimespanPicker } from "../TimespanPicker/hooks";

import "./styles.scss";

type IDownloadActivityLogsProps = {
  organizationId: string;
  locations: LocationData[];
};

export const DownloadActivityLogs: React.FC<IDownloadActivityLogsProps> = ({
  locations,
  organizationId,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`dataDownloadPage.downloadActivityLogs.${key}`);

  // Query organization data based on the organizationId from props
  const { data: organization } = useOrganization(organizationId);

  const timespan = useTimespanPicker();

  const queryKey = useMemo(
    () => [
      "download-activity",
      timespan.timespan,
      locations.map((location) => location.id.toString()),
    ],
    [timespan, locations]
  );
  const queryFunction = useCallback(async () => {
    const data = await DataSvc.getFacilityDataActivityLogs({
      organizationId,
      timespan: timespan.timespan,
      locations,
    });

    // Create facility mapping for ID insertion
    const facilityMapping = new Map<string, string | number>();
    locations.forEach((loc) => {
      facilityMapping.set(loc.name, loc.id);
    });

    // Create organization mapping for ID insertion
    // orgMapping key can be any placeholder since we only use the ID
    const orgMapping = new Map<string, string | number>([
      ["org", organizationId],
    ]);

    // Insert organization and facility IDs into the CSV
    const processedData = insertOrgAndFacilityIds(
      data,
      orgMapping,
      facilityMapping
    );

    // Build standardized filename
    const filename = buildCsvFilename(
      {
        startDate: timespan.timespan.startDate?.format("YYYY-MM-DD"),
        endDate: timespan.timespan.endDate?.format("YYYY-MM-DD"),
        organizationName: organization?.name,
        organizationId,
        contentDescription: "activity_logs",
      },
      _t
    );

    return { filename, content: processedData };
  }, [timespan, locations, organizationId, _t, organization]);

  return (
    <div className="white-border-panel download-activity-logs">
      <h3>{t("download_activity_logs")}</h3>
      <div className="content">
        <div className="download-activity-logs-controls">
          <TimespanPicker timespan={timespan} />
        </div>
        <TextFileDownload queryKey={queryKey} queryFunction={queryFunction} />
      </div>
    </div>
  );
};
